/** Fallback taxonomy — live lists come from GET /api/catalog/taxonomy via LocaleContext. */
export const SHOP_CATEGORIES = [
  "All",
  "Electronics",
  "Computers",
  "Phones",
  "Home",
  "Kitchen",
  "Furniture",
  "Beauty",
  "Health",
  "Sports",
  "Outdoors",
  "Toys",
  "Baby",
  "Pets",
  "Automotive",
  "Books",
  "Office",
  "Garden",
  "Grocery",
  "Jewelry",
  "Tools",
  "Dresses",
  "Tops",
  "Bottoms",
  "Outerwear",
  "Footwear",
  "Accessories",
  "Suits",
  "Ethnicwear",
];

export const BROWSE_CATEGORIES = SHOP_CATEGORIES.filter((c) => c !== "All");

const tree = (id, label, names) => ({
  id,
  label,
  children: names.map((name) => ({ id: name, label: name })),
});

export const SHOP_TREE = [
  tree("fashion", "Fashion", ["Dresses", "Tops", "Bottoms", "Outerwear", "Footwear", "Accessories", "Suits", "Ethnicwear"]),
  tree("electronics", "Electronics", ["Electronics", "Computers", "Phones"]),
  tree("home", "Home", ["Home", "Kitchen", "Furniture", "Garden", "Office"]),
  tree("beauty", "Beauty", ["Beauty", "Health", "Jewelry"]),
  tree("sports", "Sports", ["Sports", "Outdoors"]),
  tree("grocery", "Grocery & daily essentials", ["Grocery"]),
  tree("baby", "Baby products", ["Baby"]),
  tree("kids", "Kids", ["Toys"]),
  tree("more", "More", ["Pets", "Automotive", "Books", "Tools"]),
];

const CATEGORY_FAMILIES = {
  electronics: ["Electronics", "Computers", "Phones"],
  home: ["Home", "Kitchen", "Furniture", "Garden", "Office"],
  beauty: ["Beauty", "Health"],
  sports: ["Sports", "Outdoors"],
  kids: ["Toys", "Baby"],
  pets: ["Pets"],
  auto: ["Automotive"],
  media: ["Books"],
  grocery: ["Grocery"],
  jewelry: ["Jewelry"],
  tools: ["Tools"],
  apparel: ["Dresses", "Tops", "Bottoms", "Outerwear", "Footwear", "Accessories", "Suits", "Ethnicwear"],
};

export const APPAREL_CATEGORIES = CATEGORY_FAMILIES.apparel;

const FAMILY_BY_TITLE = Object.fromEntries(
  Object.entries(CATEGORY_FAMILIES).flatMap(([fam, titles]) => titles.map((t) => [t.toLowerCase(), fam])),
);

const APPAREL_HINTS = ["dress", "gown", "shirt", "blouse", "skirt", "jean", "heel", "sneaker", "sandal", "handbag", "lehenga", "saree", "kurta", "coat", "hoodie"];
const ELECTRONICS_HINTS = ["laptop", "computer", "phone", "smartphone", "tablet", "tv", "headphone", "camera", "monitor", "charger", "macbook", "iphone", "ipad", "electronics"];
const BROAD_CATEGORIES = new Set(["Electronics", "Home"]);
const CATEGORY_HINTS = {
  Electronics: ELECTRONICS_HINTS,
  Computers: ["laptop", "computer", "monitor", "keyboard", "desktop", "chromebook", "macbook"],
  Phones: ["smartphone", "iphone", "android", "galaxy", "pixel"],
  Home: ["vacuum", "pillow", "cushion", "rug", "lamp", "decor"],
  Kitchen: ["kitchen", "cookware", "coffee", "blender", "fryer", "dinnerware"],
  Furniture: ["furniture", "sofa", "couch", "desk", "dresser", "wardrobe", "nightstand", "bookshelf", "bench"],
  Beauty: ["beauty", "skincare", "makeup", "serum", "moisturizer", "lipstick"],
  Health: ["vitamin", "supplement", "wellness", "thermometer"],
  Sports: ["fitness", "yoga", "dumbbell", "treadmill", "bicycle", "soccer", "basketball", "tennis", "gym"],
  Outdoors: ["camping", "hiking", "tent", "lantern", "backpack", "cooler"],
  Toys: ["toy", "lego", "doll", "playset", "puzzle"],
  Baby: ["baby", "stroller", "crib", "pacifier", "diaper", "swaddle"],
  Pets: ["litter", "leash", "kibble", "aquarium"],
  Automotive: ["automotive", "tire", "windshield", "brake", "wiper"],
  Books: ["novel", "hardcover", "textbook", "fiction", "memoir"],
  Office: ["printer", "stapler", "stationery", "office", "toner", "whiteboard"],
  Garden: ["planter", "potting", "hose", "patio", "lawn", "pruner", "trowel"],
  Grocery: ["snack", "cereal", "pantry", "granola", "pasta"],
  Jewelry: ["necklace", "ring", "earring", "bracelet", "pendant", "jewelry"],
  Tools: ["drill", "screwdriver", "wrench", "hammer", "pliers"],
  Dresses: ["dress", "gown"],
  Tops: ["shirt", "blouse", "tee", "sweater", "hoodie", "flannel"],
  Bottoms: ["jean", "trouser", "pants", "skirt", "shorts", "legging", "chinos"],
  Outerwear: ["coat", "jacket", "parka", "trench", "puffer"],
  Footwear: ["shoe", "sneaker", "heel", "boot", "loafer", "sandal", "oxford"],
  Accessories: ["bag", "belt", "hat", "scarf", "dupatta", "wallet", "sunglasses", "clutch", "handbag"],
  Suits: ["suit", "blazer", "tuxedo", "waistcoat"],
  Ethnicwear: ["saree", "sari", "lehenga", "kurta", "salwar", "anarkali", "sherwani"],
};

export function productFamily(p) {
  return FAMILY_BY_TITLE[String(p?.category || "").toLowerCase()] || null;
}

export function queryLooksApparel(q) {
  const hay = String(q || "").toLowerCase();
  return APPAREL_HINTS.some((w) => new RegExp(`\\b${w}\\b`).test(hay));
}

const TITLE_STOP = new Set([
  "the", "and", "for", "with", "a", "an", "from", "this", "that", "into",
]);
const GENERIC_TITLE = new Set([
  "sofa", "chair", "table", "desk", "lamp", "dress", "gown", "shirt", "laptop",
  "chromebook", "phone", "case", "cover", "bag", "shoe", "sneaker", "seater",
  "inch", "black", "white", "silver", "two", "three", "four", "five",
]);

export function titleTokens(text) {
  return new Set(
    (String(text || "").toLowerCase().match(/[a-z0-9]+/g) || []).filter((t) => {
      if (TITLE_STOP.has(t)) return false;
      if (t.length > 2) return true;
      return t.length === 2 && /\d/.test(t);
    }),
  );
}

export function titlesLookAlike(left, right) {
  const a = titleTokens(left);
  const b = titleTokens(right);
  if (!a.size || !b.size) return false;
  const distinctive = [...a].filter((t) => !GENERIC_TITLE.has(t));
  const hit = distinctive.filter((t) => b.has(t));
  if (!hit.length) return false;
  const models = distinctive.filter((t) => /\d/.test(t));
  if (models.length) {
    const modelHit = models.filter((t) => b.has(t)).length;
    return modelHit >= (models.length >= 2 ? 2 : 1);
  }
  return true;
}

/** Keep live store rows that look like this listing — drop title-search cousins. */
export function offersForSameItem(offers, product) {
  const title = product?.title || "";
  const rows = [];
  const seen = new Set();
  for (const o of offers || []) {
    if (o?.title && title && !titlesLookAlike(title, o.title)) continue;
    const price = Number(o?.price);
    const store = String(o?.seller_name || o?.source || "store").toLowerCase().trim();
    const key = `${store}|${Number.isFinite(price) ? price.toFixed(2) : ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    rows.push(o);
  }
  return rows;
}

export function productMatchesQuery(p, q) {
  const toks = String(q || "").toLowerCase().match(/[a-z0-9]+/g) || [];
  const keep = toks.filter((t) => t.length > 2 && !["the", "and", "for", "with"].includes(t));
  if (!keep.length) return true;
  const hay = [p?.title, p?.brand, p?.category, p?.seller_name, p?.store].filter(Boolean).join(" ").toLowerCase();
  return keep.every((t) => new RegExp(`\\b${t}\\b`).test(hay) || (t.endsWith("s") && hay.includes(t.slice(0, -1))));
}

export function productMatchesCategory(p, category) {
  if (!category || category === "All") return true;
  const want = String(category);
  const have = String(p?.category || "");
  const wantFam = FAMILY_BY_TITLE[want.toLowerCase()];
  const haveFam = FAMILY_BY_TITLE[have.toLowerCase()];
  const hay = [p?.title, p?.brand, p?.category, p?.seller_name].filter(Boolean).join(" ").toLowerCase();
  const hit = (word) => new RegExp(`\\b${String(word).replace(/[.*+?^${}()|[\\]\\\\]/g, "\\$&")}\\b`).test(hay);
  if (have.toLowerCase() === want.toLowerCase()) {
    if (wantFam !== "apparel" && APPAREL_HINTS.some(hit)) return false;
    return true;
  }
  const hinted = (CATEGORY_HINTS[want] || []).some(hit);
  if (hinted) {
    if (wantFam === "apparel" && haveFam && haveFam !== "apparel") return false;
    if (wantFam !== "apparel" && haveFam === "apparel") return false;
    return true;
  }
  if (wantFam === "apparel") return false;
  if (haveFam === "apparel" || APPAREL_HINTS.some(hit)) return false;
  if (BROAD_CATEGORIES.has(want)) {
    if (haveFam) return haveFam === wantFam;
    return wantFam === "electronics" ? ELECTRONICS_HINTS.some(hit) || !APPAREL_HINTS.some(hit) : true;
  }
  return false;
}

export const NAV_SHOP_LINKS = [
  { to: "/shop", label: "New" },
  { to: "/shop?category=Electronics", label: "Electronics" },
  { to: "/shop?category=Computers", label: "Computers" },
  { to: "/shop?category=Phones", label: "Phones" },
  { to: "/shop?category=Home", label: "Home" },
  { to: "/shop?category=Kitchen", label: "Kitchen" },
  { to: "/shop?category=Beauty", label: "Beauty" },
  { to: "/shop?category=Sports", label: "Sports" },
  { to: "/shop?category=Toys", label: "Toys" },
  { to: "/shop?category=Automotive", label: "Automotive" },
  { to: "/shop?category=Books", label: "Books" },
  { to: "/shop?category=Dresses", label: "Dresses" },
  { to: "/shop?category=Footwear", label: "Footwear" },
];
